import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Share,
  Alert,
  StatusBar,
  Keyboard,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuthStore } from '@/store/authStore';
import { useFocusEffect } from '@react-navigation/native';
import {
  getGroupMessages,
  getEarlierMessages,
  sendMessage,
  subscribeToMessages,
  pollMessageEnrichment,
  Message,
  getReactionsForMessages,
  toggleMessageReaction,
  getPreviewsForMessages,
  MessageReactionSummary,
  LinkPreview,
} from '@/services/chat/chatService';
import { getGroupInviteCode, getGroupMembers, GroupMember } from '@/services/groups/groupService';
import { getFullStockConversationHistory } from '@/services/history/searchHistoryService';
import { polygonMarketDataService } from '@/services/api/polygonMarketData.service';
import { stockDataService } from '@/services/api/stockData.service';
import { perplexityService } from '@/services/api/perplexity.service';
import { tickerDetectionService } from '@/services/stock/tickerDetectionService';
import { MessageBubble } from './components/MessageBubble';
import { MessageInput } from './components/MessageInput';
import { GroupOutlookModal } from './components/GroupOutlookModal';
import { GroupMembersModal } from './components/GroupMembersModal';
import { MuteModal } from './components/MuteModal';
import { TabBar, TabType } from './components/TabBar';
import { ArtifactsSidebar } from './components/ArtifactsSidebar';
import { PulseTab } from './components/PulseTab';
import { useNotifications } from '@/hooks/useNotifications';
import { useVisualViewport } from '@/hooks/useVisualViewport';
import { supabase } from '@/services/supabase/supabaseClient';
import { StockDetailModal } from '@/screens/stock/StockDetailModal';

interface GroupChatScreenProps {
  route: {
    params: {
      groupId: string;
      groupName: string;
    };
  };
  navigation: any;
}

export const GroupChatScreen: React.FC<GroupChatScreenProps> = ({ route, navigation }) => {
  const { groupId, groupName } = route.params;
  const { user } = useAuthStore();
  const insets = useSafeAreaInsets();
  const { setViewingGroup } = useNotifications();
  const visualViewportHeight = useVisualViewport();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('chat');
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([]);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [showOutlookModal, setShowOutlookModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showMuteModal, setShowMuteModal] = useState(false);
  const [showStockDetailModal, setShowStockDetailModal] = useState(false);
  const [selectedStockTicker, setSelectedStockTicker] = useState<string>('');
  const [selectedStockCompany, setSelectedStockCompany] = useState<string>('');
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const flatListRef = useRef<FlatList>(null);
  const enrichmentTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const hasScrolledToBottomRef = useRef<boolean>(false);
  const isNearBottomRef = useRef<boolean>(true);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [reactionsMap, setReactionsMap] = useState<Record<string, MessageReactionSummary>>({});
  const [previewsMap, setPreviewsMap] = useState<Record<string, LinkPreview[]>>({});
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load initial messages and group members
  useEffect(() => {
    console.log(`💬 [GroupChat] Initializing group chat: ${groupName} (ID: ${groupId})`);
    // Reset scroll flags when group changes
    hasScrolledToBottomRef.current = false;
    isNearBottomRef.current = true;
    setShowScrollToBottom(false);
    loadMessages();
    loadGroupMembers();
  }, [groupId]);

  // Track viewing group for notifications and reset unread count
  useEffect(() => {
    console.log(`📍 [GroupChat] Setting viewing group: ${groupId}`);

    // Tell notification system we're viewing this group (prevents notifications)
    setViewingGroup(groupId);

    // Reset unread count when user views group
    if (user?.id) {
      supabase
        .rpc('reset_unread_count', {
          p_user_id: user.id,
          p_group_id: groupId,
        })
        .then(() => {
          console.log(`✅ [GroupChat] Unread count reset for group: ${groupId}`);
        })
        .catch(error => {
          console.error(`❌ [GroupChat] Failed to reset unread count:`, error);
        });
    }

    // Cleanup: clear viewing group when leaving chat
    return () => {
      console.log(`📍 [GroupChat] Clearing viewing group: ${groupId}`);
      setViewingGroup(null);
    };
  }, [groupId, user?.id, setViewingGroup]);

  // Keyboard event listeners for smooth message scrolling
  useEffect(() => {
    // On web, use visual viewport; on native, use keyboard events
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && visualViewportHeight > 0) {
        const windowHeight = window.innerHeight;
        const calculatedKeyboardHeight = Math.max(0, windowHeight - visualViewportHeight);

        if (calculatedKeyboardHeight !== keyboardHeight) {
          console.log(
            `⌨️ [GroupChat] Web viewport changed - Window: ${windowHeight}px, Visual: ${visualViewportHeight}px, Keyboard: ${calculatedKeyboardHeight}px`
          );
          setKeyboardHeight(calculatedKeyboardHeight);

          // Auto-scroll to bottom when keyboard opens (if near bottom)
          if (calculatedKeyboardHeight > 0 && isNearBottomRef.current) {
            setTimeout(() => {
              flatListRef.current?.scrollToEnd({ animated: true });
            }, 100);
          }
        }
      }
      return; // No cleanup needed for web
    }

    // Native keyboard listeners
    const keyboardWillShowListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      e => {
        console.log(`⌨️ [GroupChat] Keyboard opening (height: ${e.endCoordinates.height}px)`);
        setKeyboardHeight(e.endCoordinates.height);

        // Auto-scroll to bottom when keyboard opens (if near bottom)
        if (isNearBottomRef.current) {
          setTimeout(() => {
            flatListRef.current?.scrollToEnd({ animated: true });
          }, 100);
        }
      }
    );

    const keyboardWillHideListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => {
        console.log(`⌨️ [GroupChat] Keyboard closing`);
        setKeyboardHeight(0);
      }
    );

    return () => {
      keyboardWillShowListener.remove();
      keyboardWillHideListener.remove();
    };
  }, [visualViewportHeight, keyboardHeight]);

  // Reload messages when screen comes into focus (fixes navigation state issues)
  useFocusEffect(
    useCallback(() => {
      console.log(`🔄 [GroupChat] Screen focused - reloading messages for group: ${groupId}`);
      loadMessages();
    }, [groupId])
  );

  const loadGroupMembers = async () => {
    console.log(`👥 [GroupChat] Loading group members for: ${groupId}`);
    setMembersLoading(true);
    setMembersError(null);

    const { data, error: loadError } = await getGroupMembers(groupId);

    if (loadError) {
      console.error(`❌ [GroupChat] Failed to load members:`, loadError);
      setMembersError('Failed to load members');
    } else {
      console.log(`✅ [GroupChat] Loaded ${data?.length || 0} members`);
      setGroupMembers(data || []);
    }

    setMembersLoading(false);
  };

  // Fetch reactions and link previews for current messages
  const hydrateMessageExtras = async (msgs: Message[]) => {
    const ids = msgs.map(m => m.id);
    const [reac, prev] = await Promise.all([
      user?.id
        ? getReactionsForMessages(ids, user.id)
        : Promise.resolve({} as Record<string, MessageReactionSummary>),
      getPreviewsForMessages(ids),
    ]);
    setReactionsMap(reac);
    setPreviewsMap(prev);
  };

  // Subscribe to real-time messages
  useEffect(() => {
    console.log(`🔄 [GroupChat] Setting up real-time subscription for group: ${groupId}`);

    const unsubscribe = subscribeToMessages(
      groupId,
      // INSERT handler: New messages
      newMessage => {
        console.log(
          `📨 [GroupChat] Received new message from ${newMessage.user?.name || 'unknown'}: "${newMessage.content.substring(0, 50)}..."`
        );

        // Add message if not already in list (deduplicate by ID to prevent duplicates from optimistic add)
        setMessages(prev => {
          const exists = prev.some(msg => msg.id === newMessage.id);
          if (exists) {
            console.log(
              `⏩ [GroupChat] Message ${newMessage.id} already in list, skipping duplicate`
            );
            return prev;
          }
          console.log(`✅ [GroupChat] Adding new message ${newMessage.id} to list`);
          return [...prev, newMessage];
        });

        // Scroll to bottom to show new message
        setTimeout(() => {
          flatListRef.current?.scrollToEnd({ animated: true });
        }, 100);
      },
      // UPDATE handler: Metadata enrichment (real-time)
      updatedMessage => {
        console.log(
          `🔄 [GroupChat] Message metadata updated via real-time (ID: ${updatedMessage.id}) - ${updatedMessage.metadata.extracted_tickers?.length || 0} tickers detected`
        );

        // Cancel polling timer if exists (real-time worked!)
        const timer = enrichmentTimersRef.current.get(updatedMessage.id);
        if (timer) {
          clearTimeout(timer);
          enrichmentTimersRef.current.delete(updatedMessage.id);
          console.log(
            `✅ [GroupChat] Cancelled polling timer (real-time worked for ${updatedMessage.id})`
          );
        }

        // Update message in state
        setMessages(prev => prev.map(msg => (msg.id === updatedMessage.id ? updatedMessage : msg)));
      }
    );

    return () => {
      console.log(`🔌 [GroupChat] Unsubscribing from real-time messages for group: ${groupId}`);
      unsubscribe();
    };
  }, [groupId, user?.id]);

  // Subscribe to reactions and link preview mappings (realtime)
  useEffect(() => {
    const reactionsChannel = supabase
      .channel(`reactions:${groupId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'message_reaction' },
        payload => {
          const r: any = (payload as any).new || (payload as any).old;
          const messageId = r?.message_id;
          if (!messageId) return;
          setReactionsMap(prev => {
            const next = { ...prev };
            const cur = next[messageId] || { counts: {}, mine: [] };
            const et = (payload as any).eventType;
            if (et === 'INSERT') {
              cur.counts[r.emoji] = (cur.counts[r.emoji] || 0) + 1;
              if (r.user_id === user?.id && !cur.mine.includes(r.emoji)) cur.mine.push(r.emoji);
            } else if (et === 'DELETE') {
              cur.counts[r.emoji] = Math.max(0, (cur.counts[r.emoji] || 1) - 1);
              if (r.user_id === user?.id) cur.mine = cur.mine.filter(e => e !== r.emoji);
            }
            next[messageId] = cur;
            return next;
          });
        }
      )
      .subscribe();

    const previewsChannel = supabase
      .channel(`previews:${groupId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'message_link_preview' },
        async payload => {
          const m: any = (payload as any).new;
          const messageId = m?.message_id;
          if (!messageId) return;
          const map = await getPreviewsForMessages([messageId]);
          setPreviewsMap(prev => ({ ...prev, ...map }));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(reactionsChannel);
      supabase.removeChannel(previewsChannel);
    };
  }, [groupId, user?.id]);

  // Filter messages when selectedTicker changes
  useEffect(() => {
    if (selectedTicker === null) {
      console.log(`🔍 [GroupChat] Showing all messages (${messages.length} total)`);
      setFilteredMessages(messages);
    } else {
      // Filter messages that contain the selected ticker (in content OR metadata)
      const filtered = messages.filter(msg => {
        const normalizedContent = msg.content.toUpperCase();
        const normalizedTicker = selectedTicker.toUpperCase();

        // Check if ticker is explicitly in message content
        // Match either $TICKER or whole-word TICKER (avoid substring matches in words/URLs)
        const wordBoundary = new RegExp(`(?:^|[^A-Z0-9])${normalizedTicker}(?:[^A-Z0-9]|$)`);
        const inContent =
          normalizedContent.includes(`$${normalizedTicker}`) ||
          wordBoundary.test(normalizedContent);

        // Check if ticker was detected via intelligent extraction (ALL sources)
        const inMetadata =
          (msg.metadata?.extracted_tickers || []).some(
            (t: string) => (t || '').toUpperCase() === normalizedTicker
          ) ||
          (msg.metadata?.inherited_tickers || []).some(
            (t: string) => (t || '').toUpperCase() === normalizedTicker
          ) ||
          (msg.metadata?.manual_tickers || []).some(
            (t: string) => (t || '').toUpperCase() === normalizedTicker
          );

        const matches = inContent || inMetadata;

        // Debug: Log metadata for all messages when filtering by INTC
        if (selectedTicker.toUpperCase() === 'INTC') {
          console.log(`🔍 [GroupChat] Checking message ${msg.id.substring(0, 8)}:`, {
            content: msg.content.substring(0, 40),
            extracted: msg.metadata?.extracted_tickers,
            inherited: msg.metadata?.inherited_tickers,
            manual: msg.metadata?.manual_tickers,
            matches,
          });
        }

        return matches;
      });
      console.log(
        `🔍 [GroupChat] Filtering by ticker $${selectedTicker}: ${filtered.length}/${messages.length} messages match`
      );
      setFilteredMessages(filtered);
    }
  }, [messages, selectedTicker]);

  const loadMessages = async () => {
    console.log(`📥 [GroupChat] Loading messages for group: ${groupId}`);
    setError(null);
    const startTime = Date.now();

    const { data, error: loadError } = await getGroupMessages(groupId);

    if (loadError) {
      console.error(`❌ [GroupChat] Failed to load messages:`, loadError);
      setError('Failed to load messages. Pull to retry.');
      setHasMoreMessages(false);
    } else {
      const loadTime = Date.now() - startTime;
      console.log(`✅ [GroupChat] Loaded ${data?.length || 0} messages in ${loadTime}ms`);
      setMessages(data || []);
      // If we got exactly 500 messages, there might be more
      setHasMoreMessages((data?.length || 0) >= 500);
      try {
        await hydrateMessageExtras(data || []);
      } catch {}
    }

    setLoading(false);
  };

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMoreMessages || messages.length === 0) {
      return;
    }

    console.log(`📥 [GroupChat] Loading earlier messages...`);
    setLoadingMore(true);

    // Get the timestamp of the oldest message
    const oldestMessage = messages[0];
    const beforeTimestamp = oldestMessage.created_at;

    const {
      data,
      error: loadError,
      hasMore,
    } = await getEarlierMessages(groupId, beforeTimestamp, 50);

    if (loadError) {
      console.error(`❌ [GroupChat] Failed to load earlier messages:`, loadError);
      setError('Failed to load earlier messages.');
    } else if (data && data.length > 0) {
      console.log(`✅ [GroupChat] Loaded ${data.length} earlier messages (hasMore: ${hasMore})`);
      // Prepend earlier messages to the beginning of the array
      setMessages(prev => [...data, ...prev]);
      setHasMoreMessages(hasMore);
      try {
        await hydrateMessageExtras(data);
      } catch {}
    } else {
      console.log(`ℹ️ [GroupChat] No more earlier messages to load`);
      setHasMoreMessages(false);
    }

    setLoadingMore(false);
  };

  const onRefresh = async () => {
    console.log(`🔄 [GroupChat] User initiated refresh`);
    setRefreshing(true);
    await loadMessages();
    setRefreshing(false);
  };

  /**
   * Handle @AI mentions - AI responds in group chat
   * NOW SHOWS USER'S QUESTION: Saves user's @AI question first, then links AI response to it
   * Automatically extracts tickers and creates artifacts
   */
  const handleAIMention = async (query: string) => {
    console.log(`🤖 [GroupChat] Processing @AI mention: "${query}"`);

    try {
      // STEP 1: Save the user's @AI question as a regular message
      console.log(`💬 [GroupChat] Saving user's @AI question: "${query}"`);
      const { data: userQuestion, error: questionError } = await sendMessage(
        groupId,
        `@AI ${query}`,
        {}
      );

      if (questionError || !userQuestion) {
        setError('Failed to post question');
        console.error(`❌ [GroupChat] Failed to save user question:`, questionError);
        return;
      }

      // Add user's question to UI
      setMessages(prev => [...prev, userQuestion]);

      // Show typing indicator (use a temp message)
      const typingMessage: Message = {
        id: `ai-typing-${Date.now()}`,
        group_id: groupId,
        user_id: 'AI_BOT',
        content: 'AI is thinking...',
        created_at: new Date().toISOString(),
        reply_to_message_id: userQuestion.id, // Link to user's question
        user: {
          id: 'AI_BOT',
          name: 'AI Assistant',
          email: 'ai@app.com',
          avatar_url: null,
        },
        metadata: { isTyping: true },
      };

      setMessages(prev => [...prev, typingMessage]);

      // STEP 2: Extract tickers from user query (natural language)
      const tickerResult = await tickerDetectionService.extractMultipleTickers(query);
      console.log(`📊 [GroupChat] Extracted tickers from query:`, tickerResult.tickers);

      // STEP 3: Get AI response
      const aiResponse = await perplexityService.searchMarketInsight(query);

      if (aiResponse.error || !aiResponse.data) {
        // Remove typing indicator
        setMessages(prev => prev.filter(m => m.id !== typingMessage.id));

        // Show error
        setError(`AI Error: ${aiResponse.error?.message || 'Failed to get response'}`);
        return;
      }

      // STEP 4: Extract tickers from AI response
      const responseTickerResult = await tickerDetectionService.extractMultipleTickers(
        aiResponse.data.content
      );
      console.log(
        `📊 [GroupChat] Extracted tickers from AI response:`,
        responseTickerResult.tickers
      );

      // STEP 5: Combine all tickers (deduplicate)
      const allTickers = [...new Set([...tickerResult.tickers, ...responseTickerResult.tickers])];
      console.log(`📊 [GroupChat] All tickers to track:`, allTickers);

      // STEP 6: Post AI response to group with AI metadata and link to user's question
      // Note: Artifacts will be created automatically during message enrichment in chatService.ts
      console.log(
        `🤖 [GroupChat] Sending AI message (replying to user question ${userQuestion.id}):`,
        {
          isAI: true,
          query: query,
          tickers: allTickers,
          citations: aiResponse.data.citations,
        }
      );

      const { data: aiMessage, error: sendError } = await sendMessage(
        groupId,
        aiResponse.data.content,
        {
          manualTickers: allTickers, // Link to all detected tickers
          replyToMessageId: userQuestion.id, // CRITICAL: Link AI response to user's question
          customMetadata: {
            isAI: true, // This flags it as AI message
            query: query,
            tickers: allTickers,
            citations: aiResponse.data.citations,
          },
        }
      );

      // Remove typing indicator
      setMessages(prev => prev.filter(m => m.id !== typingMessage.id));

      if (sendError || !aiMessage) {
        setError('Failed to post AI response');
        console.error(`❌ [GroupChat] Failed to save AI message:`, sendError);
        return;
      }

      console.log(`✅ [GroupChat] AI message returned from DB:`, {
        id: aiMessage.id,
        user_id: aiMessage.user_id,
        reply_to_message_id: aiMessage.reply_to_message_id,
        metadata: aiMessage.metadata,
      });

      // Override user display info for AI messages (locally only)
      // The metadata.isAI flag will tell us to show this differently
      const enhancedAIMessage: Message = {
        ...aiMessage,
        user: aiMessage.user
          ? {
              ...aiMessage.user,
              name: '🤖 AI Assistant', // Display name override
            }
          : undefined,
      };

      setMessages(prev => [...prev, enhancedAIMessage]);
      // Scroll to bottom to show AI response
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);

      console.log(`✅ [GroupChat] AI response posted successfully (linked to question)`);
    } catch (error) {
      console.error(`❌ [GroupChat] AI mention error:`, error);

      setError('Failed to process AI request');
    }
  };

  /**
   * Poll for message enrichment with smart retry (5s, then 10s)
   * This is the fallback mechanism when real-time UPDATE events don't fire
   */
  const pollForEnrichment = (messageId: string) => {
    console.log(`⏰ [GroupChat] Setting up polling for message: ${messageId}`);

    // First poll at 5 seconds
    const timer1 = setTimeout(async () => {
      console.log(`🔄 [GroupChat] First poll for enrichment (5s): ${messageId}`);

      const { data: enrichedMessage, error } = await pollMessageEnrichment(messageId);

      if (error) {
        console.error(`❌ [GroupChat] Poll error:`, error);
        return;
      }

      if (enrichedMessage && enrichedMessage.metadata?.extracted_tickers?.length > 0) {
        console.log(
          `✅ [GroupChat] Enrichment complete! Tickers: ${enrichedMessage.metadata.extracted_tickers.join(', ')}`
        );
        // Update message in state
        setMessages(prev => prev.map(msg => (msg.id === messageId ? enrichedMessage : msg)));
        // Clear timer for this message
        enrichmentTimersRef.current.delete(messageId);
      } else {
        // Not enriched yet - try again at 10 seconds
        console.log(`⏳ [GroupChat] Not enriched yet, will poll again at 10s`);

        const timer2 = setTimeout(async () => {
          console.log(`🔄 [GroupChat] Second poll for enrichment (10s): ${messageId}`);

          const { data: enrichedMessage2 } = await pollMessageEnrichment(messageId);

          if (enrichedMessage2 && enrichedMessage2.metadata?.extracted_tickers?.length > 0) {
            console.log(
              `✅ [GroupChat] Enrichment complete! Tickers: ${enrichedMessage2.metadata.extracted_tickers.join(', ')}`
            );
            setMessages(prev => prev.map(msg => (msg.id === messageId ? enrichedMessage2 : msg)));
          } else {
            console.log(
              `⚠️ [GroupChat] Message still not enriched after 10s. Background processing may have failed or message has no tickers.`
            );
          }

          // Clear timer
          enrichmentTimersRef.current.delete(messageId);
        }, 5000); // 5 seconds after first poll = 10 seconds total

        enrichmentTimersRef.current.set(messageId, timer2);
      }
    }, 5000); // First poll at 5 seconds

    enrichmentTimersRef.current.set(messageId, timer1);
  };

  const handleSend = async (
    content: string,
    manualTickers?: string[],
    replyToMessageId?: string,
    mentionedUsers?: Array<{ user_id: string; name: string; position: number }>
  ) => {
    // Check for @AI mention
    if (content.trim().startsWith('@AI ')) {
      const query = content.trim().substring(4).trim(); // Remove "@AI " prefix
      if (query.length === 0) {
        setError('Please provide a question for the AI');
        return;
      }
      console.log(`🤖 [GroupChat] Detected @AI mention, routing to handleAIMention`);
      await handleAIMention(query);
      return;
    }

    // Regular message flow
    console.log(
      `📤 [GroupChat] Sending message: "${content.substring(0, 50)}${content.length > 50 ? '...' : ''}"`
    );
    if (manualTickers && manualTickers.length > 0) {
      console.log(`🏷️ [GroupChat] Manual tickers: ${manualTickers.join(', ')}`);
    }
    if (replyToMessageId) {
      console.log(`🔗 [GroupChat] Replying to message: ${replyToMessageId}`);
    }
    if (mentionedUsers && mentionedUsers.length > 0) {
      console.log(`👤 [GroupChat] Mentioning users: ${mentionedUsers.map(u => u.name).join(', ')}`);
    }

    setSending(true);
    setError(null);
    const startTime = Date.now();

    const { data, error: sendError } = await sendMessage(groupId, content, {
      manualTickers,
      replyToMessageId,
      customMetadata: mentionedUsers
        ? {
            mentioned_users: mentionedUsers,
          }
        : undefined,
    });

    if (sendError) {
      console.error(`❌ [GroupChat] Failed to send message:`, sendError);
      setError('Failed to send message. Please try again.');
    } else if (data) {
      const sendTime = Date.now() - startTime;
      console.log(`✅ [GroupChat] Message sent successfully in ${sendTime}ms (ID: ${data.id})`);

      // Add message to list
      setMessages(prev => [...prev, data]);

      // Start polling for enrichment (fallback for real-time UPDATE)
      // Skip polling if manual tickers provided (already has tickers)
      if (!manualTickers || manualTickers.length === 0) {
        pollForEnrichment(data.id);
      } else {
        console.log(`ℹ️ [GroupChat] Skipping enrichment poll (manual tickers provided)`);
      }

      // Clear reply state
      setReplyToMessage(null);
      // Scroll to bottom to show new message
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }

    setSending(false);
  };

  const handleReply = (message: Message) => {
    console.log(
      `🔗 [GroupChat] Replying to message from ${message.user?.name}: "${message.content.substring(0, 30)}..."`
    );
    setReplyToMessage(message);
  };

  const handleCancelReply = () => {
    console.log(`❌ [GroupChat] Cancelled reply`);
    setReplyToMessage(null);
  };

  const handleReplyPress = (messageId: string) => {
    console.log(`🔍 [GroupChat] Scrolling to replied message: ${messageId}`);

    // Find in filtered messages (current view)
    const filteredIndex = filteredMessages.findIndex(m => m.id === messageId);

    if (filteredIndex !== -1) {
      // Message is visible in current view - scroll and highlight
      flatListRef.current?.scrollToIndex({
        index: filteredIndex,
        animated: true,
        viewPosition: 0.5,
      });

      // Highlight for 2 seconds
      setHighlightedMessageId(messageId);
      setTimeout(() => setHighlightedMessageId(null), 2000);
    } else {
      // Message not visible (filtered out)
      const inAllMessages = messages.findIndex(m => m.id === messageId);
      if (inAllMessages !== -1) {
        Alert.alert(
          'Message Not Visible',
          'The original message is filtered out. Show all messages to see it.',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Show All',
              onPress: () => {
                setSelectedTicker(null);
                // Wait for filter to clear, then scroll
                setTimeout(() => {
                  const index = messages.findIndex(m => m.id === messageId);
                  if (index !== -1) {
                    flatListRef.current?.scrollToIndex({
                      index,
                      animated: true,
                      viewPosition: 0.5,
                    });
                    setHighlightedMessageId(messageId);
                    setTimeout(() => setHighlightedMessageId(null), 2000);
                  }
                }, 300);
              },
            },
          ]
        );
      }
    }
  };

  const handleShareInvite = async () => {
    console.log(`🔗 [GroupChat] Getting invite code for group: ${groupId}`);

    const { inviteCode, error: codeError } = await getGroupInviteCode(groupId);

    if (codeError || !inviteCode) {
      Alert.alert('Error', codeError || 'Failed to get invite code', [{ text: 'OK' }], {
        cancelable: true,
      });
      return;
    }

    console.log(`✅ [GroupChat] Got invite code: ${inviteCode}`);

    try {
      await Share.share({
        message: `Join my TradePlatform group "${groupName}"!\n\nInvite Code: ${inviteCode}\n\nDownload the app and enter this code to join!`,
        title: `Join ${groupName} on TradePlatform`,
      });
      console.log(`📤 [GroupChat] Shared invite code successfully`);
    } catch (error: any) {
      // User dismissed share sheet - not an actual error
      if (error?.message?.includes('User did not share')) {
        console.log(`ℹ️ [GroupChat] User cancelled share`);
      } else {
        console.error(`❌ [GroupChat] Failed to share:`, error);
        Alert.alert(
          'Share Failed',
          'Unable to share invite code. Please try again.',
          [{ text: 'OK' }],
          { cancelable: true }
        );
      }
    }
  };

  const handleScrollToBottom = () => {
    flatListRef.current?.scrollToEnd({ animated: true });
    setShowScrollToBottom(false);
    isNearBottomRef.current = true;
  };

  const handleScroll = (event: any) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const distanceFromBottom = contentSize.height - layoutMeasurement.height - contentOffset.y;

    // Show button if user scrolled up more than 50 pixels from bottom
    // Reduced from 200px for more conservative auto-scroll behavior
    const isNearBottom = distanceFromBottom < 50;
    isNearBottomRef.current = isNearBottom;

    // Only show button if there are messages and user is not near bottom
    if (filteredMessages.length > 0) {
      setShowScrollToBottom(!isNearBottom);
    }
  };

  const handleTickerPress = (ticker: string) => {
    console.log(`📊 [GroupChat] Opening StockDetail modal for ticker: ${ticker}`);

    // Get company name from ticker (fallback to ticker if not found)
    const companyName = ticker; // TODO: Could use a ticker-to-name service

    // Open modal instead of navigating
    setSelectedStockTicker(ticker);
    setSelectedStockCompany(companyName);
    setShowStockDetailModal(true);
  };

  const handleAskAIForTicker = async (ticker: string) => {
    console.log(`🤖 [GroupChat] Navigating to AI chat for ${ticker}`);

    // Load history for this stock (using Polygon, fallback to Alpha Vantage)
    const historyResult = await getFullStockConversationHistory(ticker);

    // Try Polygon first, fallback to Alpha Vantage
    let stockData = await polygonMarketDataService.getStockData(ticker);
    if (stockData.error || !stockData.quote) {
      console.warn(`⚠️ [GroupChat] Polygon failed, using Alpha Vantage for ${ticker}`);
      stockData = await stockDataService.getStockData(ticker);
    }

    if (historyResult.success && historyResult.messages && historyResult.messages.length > 0) {
      // Navigate with full history
      navigation.navigate('StockChat', {
        ticker: ticker,
        companyName: ticker, // Will be updated from quote
        previousMessages: historyResult.messages,
        stockData: stockData,
      });
    } else {
      // No history, navigate normally
      navigation.navigate('StockChat', {
        ticker: ticker,
        companyName: ticker, // Will be updated from quote
        stockData: stockData,
      });
    }
  };

  // Wrapper for existing outlook bar (uses selectedTicker)
  const handleAskAI = () => handleAskAIForTicker(selectedTicker!);

  const renderMessage = ({ item }: { item: Message }) => {
    // AI messages should always appear on the left, even if sent by current user
    const metadata = item.metadata as any;
    const isAI = metadata?.isAI === true;
    const isCurrentUser = !isAI && item.user_id === user?.id;
    const isHighlighted = item.id === highlightedMessageId;

    return (
      <MessageBubble
        message={item}
        isCurrentUser={isCurrentUser}
        isHighlighted={isHighlighted}
        onTickerPress={handleTickerPress}
        onReply={handleReply}
        onReplyPress={handleReplyPress}
        currentUserId={user?.id}
        reactions={reactionsMap[item.id]}
        onToggleReaction={async emoji => {
          setReactionsMap(prev => {
            const cur = prev[item.id] || { counts: {}, mine: [] };
            const mineHas = cur.mine.includes(emoji);
            const nextCounts = { ...cur.counts };
            if (mineHas) nextCounts[emoji] = Math.max(0, (nextCounts[emoji] || 1) - 1);
            else nextCounts[emoji] = (nextCounts[emoji] || 0) + 1;
            return {
              ...prev,
              [item.id]: {
                counts: nextCounts,
                mine: mineHas ? cur.mine.filter(e => e !== emoji) : [...cur.mine, emoji],
              },
            };
          });
          const { error } = await toggleMessageReaction(item.id, emoji);
          if (error) {
            // leave to realtime to correct; avoid double-toggling here to prevent flicker
            console.warn('[GroupChat] toggle reaction failed', error);
          }
        }}
        previews={previewsMap[item.id]}
      />
    );
  };

  // Extract recent tickers from conversation (last 20 messages)
  const recentTickers = Array.from(
    new Set(
      messages
        .slice(-20)
        .flatMap(m => {
          const metadata = m.metadata as any;
          return [
            ...(metadata?.extracted_tickers || []),
            ...(metadata?.manual_tickers || []),
            ...(metadata?.inherited_tickers || []),
          ];
        })
        .filter(Boolean)
    )
  ).slice(0, 10); // Limit to 10 most recent unique tickers

  // Extract discussed stocks with mention counts for modal
  const discussedStocks = React.useMemo(() => {
    const tickerMap = new Map<string, { ticker: string; companyName: string; count: number }>();

    // Count mentions of each ticker in recent messages
    messages.slice(-50).forEach(m => {
      const metadata = m.metadata as any;
      const tickers = [
        ...(metadata?.extracted_tickers || []),
        ...(metadata?.manual_tickers || []),
        ...(metadata?.inherited_tickers || []),
      ].filter(Boolean);

      tickers.forEach((ticker: string) => {
        const existing = tickerMap.get(ticker);
        if (existing) {
          existing.count++;
        } else {
          tickerMap.set(ticker, {
            ticker,
            companyName: ticker, // TODO: Could use a ticker-to-name service
            count: 1,
          });
        }
      });
    });

    // Convert to array and sort by mention count
    return Array.from(tickerMap.values())
      .sort((a, b) => b.count - a.count)
      .map(item => ({
        ticker: item.ticker,
        companyName: item.companyName,
        mentionCount: item.count,
      }));
  }, [messages]);

  // Show loading until messages are fetched - this ensures FlatList mounts with data ready
  // This is critical for proper initial scroll positioning
  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
        <StatusBar
          barStyle="light-content"
          backgroundColor={Colors.background.primary}
          translucent={true}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Colors.accent.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <StatusBar
        barStyle="light-content"
        backgroundColor={Colors.background.secondary}
        translucent={true}
      />
      <KeyboardAvoidingView
        style={[
          styles.keyboardAvoid,
          Platform.OS === 'web' && visualViewportHeight > 0 && { maxHeight: visualViewportHeight },
        ]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="chevron-back" size={24} color={Colors.text.primary} />
            </TouchableOpacity>

            <View style={styles.headerContent}>
              <Text style={styles.headerTitle} numberOfLines={1} ellipsizeMode="tail">
                {groupName}
              </Text>
              <Text style={styles.headerSubtitle}>
                {filteredMessages.length !== messages.length
                  ? `${filteredMessages.length} of ${messages.length} messages`
                  : `${messages.length} message${messages.length !== 1 ? 's' : ''}`}
              </Text>
            </View>

            <View style={styles.headerActions}>
              <TouchableOpacity
                style={styles.chartButton}
                onPress={() =>
                  navigation.navigate('CollaborativeChart', {
                    groupId,
                    symbol: selectedTicker || 'SPY',
                    groupName,
                  })
                }
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="bar-chart-outline" size={22} color={Colors.accent.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.headerButton}
                onPress={() => setShowMuteModal(true)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="notifications-outline" size={22} color={Colors.text.secondary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.headerButton}
                onPress={() => setShowMembersModal(true)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="people-outline" size={22} color={Colors.text.secondary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.headerButton}
                onPress={handleShareInvite}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="share-outline" size={22} color={Colors.text.secondary} />
              </TouchableOpacity>
            </View>
          </View>

          {/* Stock Action Bar (when ticker is filtered) */}
          {selectedTicker && (
            <View style={styles.outlookBar}>
              <View style={styles.tickerBadge}>
                <Text style={styles.tickerBadgeText}>${selectedTicker}</Text>
              </View>
              <View style={styles.outlookBarActions}>
                <TouchableOpacity
                  style={styles.outlookBarButton}
                  onPress={() => setShowOutlookModal(true)}
                  activeOpacity={0.8}
                >
                  <Ionicons name="analytics-sharp" size={16} color={Colors.ai.primary} />
                  <Text style={styles.outlookBarButtonText}>Outlook</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.outlookBarButton, styles.askAIButton]}
                  onPress={handleAskAI}
                  activeOpacity={0.8}
                >
                  <Ionicons name="sparkles" size={16} color={Colors.accent.primary} />
                  <Text style={[styles.outlookBarButtonText, styles.askAIButtonText]}>Ask AI</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.clearFilterButton}
                  onPress={() => setSelectedTicker(null)}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons name="close-circle" size={20} color={Colors.text.tertiary} />
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>

        {/* Error Banner */}
        {error && (
          <View style={styles.errorBanner}>
            <Ionicons name="warning" size={16} color={Colors.status.error} />
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity onPress={() => setError(null)}>
              <Ionicons name="close" size={16} color={Colors.status.error} />
            </TouchableOpacity>
          </View>
        )}

        {/* Tab Bar - iOS Segmented Control Style */}
        <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Tab Content */}
        {activeTab === 'chat' && (
          <>
            <FlatList
              ref={flatListRef}
              data={filteredMessages}
              renderItem={renderMessage}
              keyExtractor={item => item.id}
              contentContainerStyle={styles.messagesList}
              showsVerticalScrollIndicator={true}
              onScroll={handleScroll}
              scrollEventThrottle={16}
              onLayout={() => {
                // Scroll to bottom ONCE when FlatList first lays out with data
                // This ensures chat opens at bottom showing latest messages
                if (!hasScrolledToBottomRef.current && filteredMessages.length > 0) {
                  console.log(`📍 [GroupChat] Initial layout - scrolling to bottom`);
                  // Use setTimeout to ensure layout is complete
                  setTimeout(() => {
                    flatListRef.current?.scrollToEnd({ animated: false });
                    hasScrolledToBottomRef.current = true;
                  }, 50);
                }
              }}
              onContentSizeChange={() => {
                // Auto-scroll to bottom only when new messages arrive AND user is near bottom
                // Don't scroll on initial load (handled by onLayout)
                if (hasScrolledToBottomRef.current && isNearBottomRef.current) {
                  setTimeout(() => {
                    flatListRef.current?.scrollToEnd({ animated: true });
                  }, 100);
                }
              }}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={onRefresh}
                  tintColor={Colors.accent.primary}
                />
              }
              ListHeaderComponent={
                <>
                  <View style={styles.listHeader} />
                  {hasMoreMessages && (
                    <View style={styles.loadMoreContainer}>
                      <TouchableOpacity
                        style={styles.loadMoreButton}
                        onPress={loadMoreMessages}
                        disabled={loadingMore}
                        activeOpacity={0.7}
                      >
                        {loadingMore ? (
                          <ActivityIndicator size="small" color={Colors.accent.primary} />
                        ) : (
                          <>
                            <Ionicons name="chevron-up" size={16} color={Colors.accent.primary} />
                            <Text style={styles.loadMoreButtonText}>Load Earlier Messages</Text>
                          </>
                        )}
                      </TouchableOpacity>
                    </View>
                  )}
                </>
              }
              ListFooterComponent={<View style={styles.listFooter} />}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <Ionicons name="chatbubbles-outline" size={80} color={Colors.text.tertiary} />
                  <Text style={styles.emptyTitle}>
                    {selectedTicker
                      ? `No messages about $${selectedTicker}`
                      : 'Start the conversation'}
                  </Text>
                  <Text style={styles.emptyText}>
                    {selectedTicker
                      ? 'No messages found with this ticker'
                      : 'Be the first to share your trading insights!'}
                  </Text>
                  {!selectedTicker && (
                    <View style={styles.emptyTips}>
                      <Text style={styles.emptyTip}>💡 Tip: Use @AI or $TSLA</Text>
                      <Text style={styles.emptyTip}>📊 Real-time collaboration</Text>
                    </View>
                  )}
                </View>
              }
            />

            {/* Floating Scroll to Bottom Button */}
            {showScrollToBottom && (
              <TouchableOpacity
                style={styles.scrollToBottomButton}
                onPress={handleScrollToBottom}
                activeOpacity={0.8}
              >
                <Ionicons name="chevron-down" size={24} color="#FFFFFF" />
              </TouchableOpacity>
            )}

            {/* Message Input (only on chat tab) */}
            <MessageInput
              onSend={handleSend}
              disabled={sending}
              sending={sending}
              replyToMessage={replyToMessage}
              onCancelReply={handleCancelReply}
              recentTickers={recentTickers}
              groupMembers={groupMembers}
              currentUserId={user?.id}
            />
          </>
        )}

        {activeTab === 'stocks' && (
          <ArtifactsSidebar
            groupId={groupId}
            selectedTicker={selectedTicker}
            onSelectTicker={setSelectedTicker}
            onClose={() => setActiveTab('chat')}
            navigation={navigation}
          />
        )}

        {activeTab === 'pulse' && <PulseTab groupId={groupId} onTickerPress={handleTickerPress} />}

        {/* Group Outlook Modal */}
        <GroupOutlookModal
          visible={showOutlookModal}
          groupId={groupId}
          ticker={selectedTicker}
          onClose={() => setShowOutlookModal(false)}
          onViewMessages={() => {
            setShowOutlookModal(false);
            // Messages are already filtered, just scroll to top
            setTimeout(() => {
              flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
            }, 300);
          }}
        />

        {/* Group Members Modal */}
        <GroupMembersModal
          visible={showMembersModal}
          onClose={() => setShowMembersModal(false)}
          members={groupMembers}
          currentUserId={user?.id || ''}
          loading={membersLoading}
          error={membersError}
        />

        {/* Mute Modal */}
        <MuteModal
          visible={showMuteModal}
          onClose={() => setShowMuteModal(false)}
          groupId={groupId}
          groupName={groupName}
          userId={user?.id || ''}
        />

        {/* Stock Detail Modal */}
        <StockDetailModal
          visible={showStockDetailModal}
          onClose={() => setShowStockDetailModal(false)}
          ticker={selectedStockTicker}
          companyName={selectedStockCompany}
          groupId={groupId}
          initialTab="outlook"
          discussedStocks={discussedStocks}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  keyboardAvoid: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    backgroundColor: Colors.background.secondary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
    paddingBottom: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 8,
  },
  backButton: {
    padding: 4,
    marginRight: 2,
  },
  headerContent: {
    flex: 1,
    marginHorizontal: 4,
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: Colors.text.primary,
    letterSpacing: 0.3,
  },
  headerSubtitle: {
    fontSize: 11,
    color: Colors.text.tertiary,
    marginTop: 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  chartButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: Colors.accent.primary + '15',
  },
  headerButton: {
    padding: 8,
    borderRadius: 8,
  },
  // Compact Outlook Bar Styles (inline with header)
  outlookBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: `${Colors.ai.primary}06`,
    borderLeftWidth: 2,
    borderLeftColor: Colors.ai.primary,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginTop: 4,
    marginHorizontal: 12,
    borderRadius: 6,
    gap: 8,
  },
  tickerBadge: {
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 5,
  },
  tickerBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.text.primary,
    letterSpacing: 0.5,
  },
  outlookBarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  outlookBarButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${Colors.ai.primary}15`,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 5,
    gap: 4,
  },
  outlookBarButtonText: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.ai.primary,
  },
  askAIButton: {
    backgroundColor: `${Colors.accent.primary}15`,
  },
  askAIButtonText: {
    color: Colors.accent.primary,
  },
  clearFilterButton: {
    padding: 3,
  },
  messagesList: {
    flexGrow: 1,
    paddingHorizontal: 12,
  },
  listHeader: {
    height: 16, // Top padding for first message
  },
  loadMoreContainer: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignItems: 'center',
  },
  loadMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${Colors.accent.primary}10`,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
    minWidth: 200,
    borderWidth: 1,
    borderColor: `${Colors.accent.primary}30`,
  },
  loadMoreButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.accent.primary,
    letterSpacing: 0.2,
  },
  listFooter: {
    height: 12, // Bottom padding for last message
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${Colors.status.error}12`,
    borderBottomWidth: 1,
    borderBottomColor: `${Colors.status.error}80`,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 12,
    color: Colors.status.error,
    letterSpacing: 0.1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingTop: 60,
  },
  emptyTitle: {
    fontSize: 19,
    fontWeight: '700',
    color: Colors.text.primary,
    marginTop: 20,
    marginBottom: 6,
    letterSpacing: 0.3,
  },
  emptyText: {
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
    marginBottom: 20,
    letterSpacing: 0.1,
  },
  emptyTips: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  emptyTip: {
    fontSize: 12,
    color: Colors.text.tertiary,
    marginVertical: 3,
    letterSpacing: 0.1,
  },
  scrollToBottomButton: {
    position: 'absolute',
    bottom: 90,
    right: 16,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: Colors.accent.primary,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
});
